import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { Subscription } from 'rxjs';

import { AlertCardComponent } from '@shared/components/alert-card/alert-card.component';
import { EstoqueLoteSerieInlineComponent } from '@shared/components/estoque-lote-serie-inline/estoque-lote-serie-inline.component';
import { LoteSaidaEstoqueModalItem } from '@shared/components/lote-saida-estoque-modal/lote-saida-estoque-modal.component';
import { SerieSaidaEstoqueModalItem } from '@shared/components/serie-saida-estoque-modal/serie-saida-estoque-modal.component';
import { AlertService, AlertType } from '@shared/services/alert.service';
import { LookupService as ProdutoLookupService } from '@shared/services/apis/produto/produto-lookup.service';
import { VendaItemRastreabilidade } from '@shared/services/models/vendas/venda-item-rastreabilidade.model';
import { VendaItemSerie } from '@shared/services/models/vendas/venda-item-serie.model';
import { LoteDisponivelLookupStruct } from '@shared/services/structs/produto/lote-disponivel-lookup.struct';

/**
 * Aba "Lote/série" do modal do item da venda (orçamento e pedido), no visual da aba do item da NF-e de
 * entrada: o painel expansível `app-estoque-lote-serie-inline` no modo `selecionar`, com os lotes ou as
 * séries com saldo do produto na localização do item (`Lookup/loteDisponivelSelect` e
 * `serieDisponivelSelect` da API.Produto).
 *
 * A lista disponível é carregada ao abrir e a cada troca de produto ou de localização; o que já está
 * selecionado no item entra nela mesmo sem saldo, para continuar visível. Em leitura, só a seleção do
 * item é exibida, sem consulta. A seleção sai por `lotesChange`/`seriesChange`; quem confere se ela
 * fecha a quantidade é o modal.
 */
@Component({
    selector: 'app-vd-item-aba-lote-serie',
    standalone: true,
    imports: [AlertCardComponent, EstoqueLoteSerieInlineComponent],
    templateUrl: './vd-item-aba-lote-serie.component.html',
    styleUrls: ['../abas-comum.css', './vd-item-aba-lote-serie.component.css'],
})
export class VdItemAbaLoteSerieComponent implements OnChanges, OnDestroy {
    @Input() public idProduto: number | null = null;
    @Input() public idLocalizacaoEstoque: number | null = null;
    /** Documento já gravado: a reserva dele volta ao saldo dos lotes e séries consultados (T21). */
    @Input() public idVenda: number | null = null;
    @Input() public nomeLocalizacaoEstoque: string = '';
    @Input() public quantidadeAlvo: number = 0;

    /** Lote e série são exclusivos no cadastro do produto: sem série, a aba é de lote. */
    @Input() public isControlaSeries: boolean = false;
    @Input() public isUnidadeMedidaFracionada: boolean = true;
    @Input() public isSomenteLeitura: boolean = false;
    @Input() public lotes: VendaItemRastreabilidade[] = [];
    @Input() public series: VendaItemSerie[] = [];

    @Output() public lotesChange = new EventEmitter<VendaItemRastreabilidade[]>();
    @Output() public seriesChange = new EventEmitter<VendaItemSerie[]>();

    public painelAberto: boolean = true;

    /** Nulo enquanto a lista carrega: o painel mostra o carregamento. */
    public disponiveis: (LoteSaidaEstoqueModalItem | SerieSaidaEstoqueModalItem)[] | null = null;
    public itensSelecionados: (LoteSaidaEstoqueModalItem | SerieSaidaEstoqueModalItem)[] = [];

    /** O lookup traz a fabricação, que o item do painel não carrega: a seleção a copia daqui. */
    private lotesDoLookup: LoteDisponivelLookupStruct[] = [];
    private carga: Subscription | undefined;

    /** A última seleção emitida: volta pelo formulário do modal e não é reenviada ao painel. */
    private selecaoEmitida: unknown[] | null = null;

    constructor(
        private produtoLookupService: ProdutoLookupService,
        private alertService: AlertService,
    ) {}

    public get tipo(): 'lote' | 'serie' {
        return this.isControlaSeries ? 'serie' : 'lote';
    }

    public get descricaoControle(): string {
        return this.isControlaSeries
            ? 'Controla número de série: selecione uma série por unidade do item.'
            : 'Controla lote e validade: distribua a quantidade do item entre os lotes com saldo na localização.';
    }

    public ngOnChanges(changes: SimpleChanges): void {
        // A seleção que a própria aba emitiu volta pelo formulário do modal; reenviá-la ao painel refaria as
        // linhas de lote e descartaria as que ainda estão sem lote ou sem quantidade.
        const selecao = this.isControlaSeries ? this.series : this.lotes;
        if (changes['isControlaSeries'] || ((changes['lotes'] || changes['series']) && selecao !== this.selecaoEmitida)) {
            this.itensSelecionados = this.isControlaSeries ? this.seriesDoPainel() : this.lotesDoPainel();
        }

        if (changes['idProduto'] || changes['idLocalizacaoEstoque'] || changes['isControlaSeries'] || changes['isSomenteLeitura']) {
            this.carregarDisponiveis();
        }
    }

    public ngOnDestroy(): void {
        this.carga?.unsubscribe();
    }

    private carregarDisponiveis(): void {
        this.carga?.unsubscribe();

        // Em leitura não há o que escolher: o painel mostra só o que o item tem.
        if (this.isSomenteLeitura || !this.idProduto) {
            this.disponiveis = [...this.itensSelecionados];
            return;
        }

        this.disponiveis = null;

        if (this.isControlaSeries) {
            this.carga = this.produtoLookupService
                .getSerieDisponivelSelect(this.idProduto, this.idLocalizacaoEstoque, this.idVenda).subscribe({
                next: (res) => {
                    if (res.isError) {
                        this.falharCarga('Não foi possível carregar as séries disponíveis do produto.');
                        return;
                    }

                    const lista: SerieSaidaEstoqueModalItem[] = (res.list ?? [])
                        .map((s) => ({ idSerie: s.idSerie, codigoSerie: s.numeroSerie, selecionada: false }));
                    const selecionadas = (this.itensSelecionados as SerieSaidaEstoqueModalItem[])
                        .filter((s) => !lista.some((d) => d.idSerie === s.idSerie));

                    this.disponiveis = [...selecionadas, ...lista];
                },
                error: () => this.falharCarga('Não foi possível carregar as séries disponíveis do produto.'),
            });
            return;
        }

        this.carga = this.produtoLookupService.getLoteDisponivelSelect(this.idProduto, this.idLocalizacaoEstoque, this.idVenda).subscribe({
            next: (res) => {
                if (res.isError) {
                    this.falharCarga('Não foi possível carregar os lotes disponíveis do produto.');
                    return;
                }

                this.lotesDoLookup = res.list ?? [];

                const lista: LoteSaidaEstoqueModalItem[] = this.lotesDoLookup.map((l) => ({
                    idLote: l.idLote,
                    codigoLote: l.numeroLote,
                    dataValidade: l.dataValidade,
                    saldoDisponivel: l.saldoDisponivel,
                    quantidadeSaida: 0,
                }));
                const selecionados = (this.itensSelecionados as LoteSaidaEstoqueModalItem[])
                    .filter((l) => !lista.some((d) => d.idLote === l.idLote));

                this.disponiveis = [...selecionados, ...lista];
            },
            error: () => this.falharCarga('Não foi possível carregar os lotes disponíveis do produto.'),
        });
    }

    private falharCarga(mensagem: string): void {
        this.alertService.show('Erro', mensagem, AlertType.error);
        this.disponiveis = [...this.itensSelecionados];
    }

    /** O lote já selecionado entra com o saldo da própria seleção: o que está no item continua disponível para ele. */
    private lotesDoPainel(): LoteSaidaEstoqueModalItem[] {
        return (this.lotes ?? []).map((l) => ({
            idLote: l.idLote,
            codigoLote: l.numeroLote ?? '',
            dataValidade: l.dataValidade ?? undefined,
            saldoDisponivel: Number(l.quantidadeLote ?? 0) || 0,
            quantidadeSaida: Number(l.quantidadeLote ?? 0) || 0,
        }));
    }

    private seriesDoPainel(): SerieSaidaEstoqueModalItem[] {
        return (this.series ?? []).map((s) => ({ idSerie: s.idSerie, codigoSerie: s.numeroSerie ?? '', selecionada: true }));
    }

    /** Lote: id, número, quantidade e datas (snapshot de exibição); série: id e número. Os ids do servidor ficam de fora. */
    public aoAlterarSelecao(itens: (LoteSaidaEstoqueModalItem | SerieSaidaEstoqueModalItem)[]): void {
        if (this.isControlaSeries) {
            const series: VendaItemSerie[] = (itens as SerieSaidaEstoqueModalItem[])
                .map((s) => ({ idSerie: s.idSerie, numeroSerie: s.codigoSerie }));
            this.selecaoEmitida = series;
            this.seriesChange.emit(series);
            return;
        }

        const anteriores = new Map((this.lotes ?? []).map((l) => [l.idLote, l]));

        const lotes: VendaItemRastreabilidade[] = (itens as LoteSaidaEstoqueModalItem[]).map((l) => {
            const doLookup = this.lotesDoLookup.find((d) => d.idLote === l.idLote);
            const anterior = anteriores.get(l.idLote);

            return {
                idLote: l.idLote,
                numeroLote: l.codigoLote,
                quantidadeLote: Number(l.quantidadeSaida ?? 0) || 0,
                dataFabricacao: doLookup?.dataFabricacao ?? anterior?.dataFabricacao ?? null,
                dataValidade: doLookup?.dataValidade ?? anterior?.dataValidade ?? null,
                codigoAgregacao: anterior?.codigoAgregacao ?? null,
            };
        });
        this.selecaoEmitida = lotes;
        this.lotesChange.emit(lotes);
    }
}
